import { useState, type FC } from "react";
import { Alert, Badge, Box, Button, Group, Stack } from "@mantine/core";
import {
  IconCheck,
  IconDental,
  IconMaximize,
  IconMessage,
} from "@tabler/icons-react";
import { OrderDesignPreview, type Order } from "@jasper/shared";
import { DesignRejectionModal } from "/@/components/DesignRejectionModal";
import { useDesignReviewActions } from "/@/components/useDesignReviewActions";
import type { OrderPageState } from "../useOrderPage";
import { OrderDetailCard } from "./OrderDetailCard";
import { getProductHeadline, ICON_STROKE } from "./order-page-v2.utils";

const DESIGN_PREVIEW_ASPECT_RATIO = "16 / 10";

const getDesignCardTitle = (order: Order): string =>
  order.products.length === 1
    ? `Design proposé · ${getProductHeadline(order.products[0])}`
    : "Design proposé";

type OrderDesignCardProps = {
  order: Order;
  state: OrderPageState;
};

export const OrderDesignCard: FC<OrderDesignCardProps> = ({ order, state }) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const review = useDesignReviewActions({
    orderId: order.id,
    refetchOrder: state.refetch,
  });
  const { openedOrderDesign, requestedOrderDesign, isReviewing } = review;
  const orderDesignId =
    openedOrderDesign?.id ?? state.design.latestOrderDesignId;

  return (
    <OrderDetailCard
      icon={IconDental}
      title={getDesignCardTitle(order)}
      rightSection={openedOrderDesign && <Badge color="amber">À valider</Badge>}
    >
      {requestedOrderDesign || !orderDesignId ? (
        <Alert color="gray">
          Le design est en cours de préparation par le laboratoire. Vous serez
          notifié dès qu’il sera prêt pour revue.
        </Alert>
      ) : (
        <Stack gap="s4">
          <Box style={{ aspectRatio: DESIGN_PREVIEW_ASPECT_RATIO }}>
            <OrderDesignPreview
              orderDesignId={orderDesignId}
              interactionsEnabled={!review.rejection.isOpened}
              isFullscreen={isFullscreen}
              onFullscreenChange={setIsFullscreen}
            />
          </Box>
          <Group gap="s3">
            {openedOrderDesign && (
              <>
                <Button
                  leftSection={<IconCheck size={14} stroke={ICON_STROKE} />}
                  loading={isReviewing}
                  onClick={review.validate}
                >
                  Valider le design
                </Button>
                <Button
                  variant="outline"
                  leftSection={<IconMessage size={14} stroke={ICON_STROKE} />}
                  loading={isReviewing}
                  onClick={review.rejection.open}
                >
                  Demander une modif.
                </Button>
              </>
            )}
            <Button
              variant="subtle"
              leftSection={<IconMaximize size={14} stroke={ICON_STROKE} />}
              onClick={() => setIsFullscreen(true)}
            >
              Plein écran
            </Button>
          </Group>
          <DesignRejectionModal review={review} />
        </Stack>
      )}
    </OrderDetailCard>
  );
};
